<template>
  <LayoutCheckout>
    <template v-if="!isVip()" v-slot:summary-sticky>
      <CheckoutSummaryStickyAccordion
        isShowLabelExpand
        class="dp-block dp-lg-none"
      >
        <template v-slot:title>
          <div class="title-semibold">
            {{ finalPrice | currency2 }}
          </div>
        </template>
        <template v-slot:content>
          <div class="section-summary-sticky-accordion">
            <CheckoutProductItems
              :data="productCartItems"
              :is-loading="cartLoading"
              :branch-type="branchType"
              :cart-delivery="cartDelivery"
              @remove="onRemoveBundleAndAdditional"
            />
            <hr class="divider-gray-83" />

            <!-- Coupon -->
            <template
              v-if="isCouponEnabled && (isMobile || isTablet)"
            >
              <CheckoutApplyCoupon key="summary-sticky" />
              <hr class="divider-gray-83" />
            </template>

            <CheckoutSummary
              :cartSummaryData="cartSummaryData"
              :isDeposit="isDeposit"
              :isPickupAtStore="!isDelivery"
              :shipDate="shipDate"
              :isShipDateHighlight="isShipDateHighlight"
              :isShowDeliveryHintMessage="true"
            />
          </div>
        </template>
      </CheckoutSummaryStickyAccordion>
    </template>

    <template v-slot:left-container>
      <CheckoutBreadcrumb />

      <CheckoutCustomerForm
        ref="checkoutCustomerForm"
        :shipDate="shipDate"
        :isShipDateHighlight="isShipDateHighlight"
      >
        <template v-if="!isVip()" v-slot>
          <div class="section-summary">
            <div v-if="productCartItems.length == 1">
              <div class="headline-m">
                รายการสั่งซื้อ
              </div>

              <CheckoutProductItems
                :data="productCartItems"
                :is-loading="cartLoading"
                :branch-type="branchType"
                :cart-delivery="cartDelivery"
                @remove="onRemoveBundleAndAdditional"
                class="mt-4"
              />
            </div>

            <PDPAccordionItem
              v-if="productCartItems.length > 1"
              isShowLabelExpand
            >
              <template v-slot:title>
                <div class="headline-m">
                  รายการสั่งซื้อ ({{ productCartItems.length }})
                </div>
              </template>
              <template v-slot:content>
                <CheckoutProductItems
                  :data="productCartItems"
                  :is-loading="cartLoading"
                  :branch-type="branchType"
                  :cart-delivery="cartDelivery"
                  @remove="onRemoveBundleAndAdditional"
                  class="mt-4"
                />
              </template>
            </PDPAccordionItem>

            <!-- Coupon -->
            <CheckoutApplyCoupon
              v-if="isCouponEnabled"
              key="summary-main"
            />

            <CheckoutSummary
              :cartSummaryData="cartSummaryData"
              :isDeposit="isDeposit"
              :isPickupAtStore="!isDelivery"
              :shipDate="shipDate"
              :isShipDateHighlight="isShipDateHighlight"
              :isShowDeliveryHintMessage="true"
            />
          </div>
        </template>
      </CheckoutCustomerForm>

      <Footer />
    </template>

    <template v-slot:right-container>
      <template v-if="isVip()">
        <div
          class="marketing-banner-html"
          v-html="marketingBannerHtml"
        ></div>
      </template>
      <template v-else>
        <CheckoutProductItems
          :data="productCartItems"
          :is-loading="cartLoading"
          :branch-type="branchType"
          :cart-delivery="cartDelivery"
          @remove="onRemoveBundleAndAdditional"
        />
        <hr class="divider-gray-83" />

        <!-- Coupon -->
        <template
          v-if="isCouponEnabled && (isDesktop || isDesktopLarge)"
        >
          <CheckoutApplyCoupon key="summary-main" />
          <hr class="divider-gray-83" />
        </template>

        <CheckoutSummary
          :cartSummaryData="cartSummaryData"
          :isDeposit="isDeposit"
          :isPickupAtStore="!isDelivery"
          :shipDate="shipDate"
          :isShipDateHighlight="isShipDateHighlight"
          :isShowDeliveryHintMessage="true"
        />
      </template>
    </template>
  </LayoutCheckout>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import config from '@/config/index'
const { showFreeBie } = config
import isBnn from '@/config/isBnn'
import colorBnn from '@/config/color'
import cartSummary from '@/mixins/cartSummary'
import screenSize from '@/mixins/screenSize'
import branchTypes from '@/config/branchTypes'
import { SHIPPING_DATE } from '@/config/index'
import moment from 'moment'
import { getVerifiedMemberId } from '@/utils/helpers'

export default {
  name: 'Checkout',

  mixins: [cartSummary, screenSize],

  inject: ['isVip'],

  components: {
    LayoutCheckout: () =>
      import('@/components/layout/LayoutCheckout'),
    CheckoutBreadcrumb: () =>
      import('@/components/studio7/CheckoutBreadcrumb'),
    CheckoutCustomerForm: () =>
      import('@/components/studio7/CheckoutCustomerForm'),
    CheckoutProductItems: () =>
      import('@/components/studio7/CheckoutProductItems'),
    CheckoutApplyCoupon: () =>
      import('@/components/studio7/CheckoutApplyCoupon'),
    CheckoutSummary: () =>
      import('@/components/studio7/CheckoutSummary'),
    PDPAccordionItem: () =>
      import('@/components/studio7/PDPAccordionItem'),

    CheckoutSummaryStickyAccordion: () =>
      import('@/components/studio7/CheckoutSummaryStickyAccordion'),

    Footer: () => import('@/components/bnn/Footer'),
  },

  data() {
    return {
      // Import
      colorBnn,
      isBnn,

      // Data
      form: {
        couponCode: '',
      },
      formCheckSummary: {
        product_id: '',
        bundle_products: [],
        delivery: false,
        is_delivery: false,
      },
      freebieListByCouponModal: false,
    }
  },

  computed: {
    ...mapState({
      selectSKU: state => state.model.selectSKU,
      campaign: state => state.campaign.current,
      cartSummaryData: state => state.cart.cartSummaryData,
      cartLoading: state => state.cart.isLoading,
      branchType: state => state.branchStore.branchType,
      cartDelivery: state => state.cart.delivery,
      marketingBannerHtml: state =>
        state.staticPage.data?.[0]?.html ?? '',
    }),

    ...mapGetters({
      bundles: 'model/getBundles',
      additionalProduct: 'model/getAdditionalProduct',
      appleCare: 'model/getAppleCare',
      cartItems: 'cart/cartItems',
      productItem: 'cart/productItem',
      priceSummary: 'cart/priceSummary',
      isSoldOut: 'model/getSoldOut',
      bundleItems: 'cart/bundleItems',
      discountOnTop: 'cart/discountOnTop',
      deliveryItems: 'cart/deliveryItems',
      getBundleDelivery: 'model/getBundleDelivery',
      accessoryBundles: 'model/getAccessoryBundles',
      // getFreebieHandraiser
      handraiserFreebies: 'model/getHandraiserFreebies' ?? [],
      productPaymentOptions: 'paymentType/getProductPaymentOptions',
    }),

    productCartItems() {
      const cartItems =
        this.cartItems.filter(item => item.type !== 'delivery') || []

      if (!showFreeBie) {
        return cartItems?.filter(item => !item?.isFreeBie)
      }
      return cartItems
    },

    isShipDateHighlight() {
      let highlight =
        this.selectSKU?.actual_date &&
        moment(this.selectSKU?.actual_date).isAfter(
          moment(SHIPPING_DATE.FIRST_LOT),
        )

      let className =
        this.selectSKU?.actual_date === SHIPPING_DATE.SECOND_LOT
          ? 'ship-date-highlight lot-2'
          : this.selectSKU?.actual_date === SHIPPING_DATE.THIRD_LOT
          ? 'ship-date-highlight lot-3'
          : ''

      return highlight ? className : ''
    },

    shipDate() {
      return this.selectSKU?.active
        ? this.productPaymentOptions?.message ||
            this.selectSKU.shipping_date ||
            this.campaign?.data?.shipdate ||
            '-'
        : 'สินค้าไม่พร้อมบริการ'
    },

    isDeposit() {
      return this.branchType === branchTypes.BRANCH_TYPE_DEPOSIT
    },

    isCouponEnabled() {
      return this.campaign?.data?.is_coupon_enabled ?? false
    },

    isDelivery() {
      return (
        this.cartDelivery === 'delivery' || this.cartDelivery === true
      )
    },

    finalPrice() {
      return this.cartSummaryData?.final_price ?? 0
    },
  },

  methods: {
    onRemoveBundleAndAdditional(item) {
      this.$store.dispatch('bundle/toggle', item)
      this.$forceUpdate()
      this.checkValidateCoupon()
      this.checkSummary()
    },

    async checkValidateCoupon() {
      if (this.form.couponCode.trim()) {
        const payload = {
          productId: this.selectSKU.id,
          couponCode: this.form.couponCode,
          bundleItems: this.bundleItems,
          // TODO: Check if paymentTypeId is needed in BU: BNN
          paymentTypeId: this.formCheckSummary.payment_type_id,
          memberId:
            getVerifiedMemberId(this.$route.params?.slug) || null,
          isStockCard: !!this.$route.query?.code || false,
        }

        await this.$store.dispatch('cart/checkCouponCode', payload)

        if (this.coupon.autoAddToCart) {
          this.HandleFreebieWithCoupon('add')
        }

        this.checkSummary()
      }
    },

    HandleFreebieWithCoupon(key) {
      if (this.coupon.autoAddToCart) {
        switch (key) {
          case 'remove':
            this.bundleItems.find(item => {
              if (item.sku === this.coupon.autoAddToCart) {
                this.$store.dispatch('bundle/remove', item)
              }
            })
            break

          default:
            this.handraiserFreebies?.find(item => {
              if (item.sku === this.coupon.autoAddToCart) {
                this.$store.dispatch('bundle/add', item)
              }
            })
            break
        }
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.marketing-banner-html {
    max-width: 100%
    overflow: hidden
}
</style>
